/**
 * Thin fetch wrapper for Aroo's API. Every response is parsed with the shared zod schema so a
 * server/client drift fails loudly in development instead of rendering nonsense.
 * The dev server proxies /api to the API (vite.config.ts); in production nginx does the same.
 *
 * Sessions are a cookie, so nothing here carries a token. A 401 from any route other than the
 * session endpoints means the session ended elsewhere (signed out on another device, expired);
 * the SessionProvider registers `onUnauthorized` to send the person back to /login.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** The API's machine-readable code (`error` in the JSON body), when it sent one. */
    public readonly code: string | null = null,
    /** The rest of the JSON body (e.g. `lockedUntil`, `minutesRemaining`). */
    public readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Query = Record<string, string | number | boolean | undefined>;

/** Anything with zod's `parse` (the shared schemas); keeps zod out of the web bundle's deps. */
interface Parser<T> {
  parse(data: unknown): T;
}

let unauthorizedHandler: (() => void) | null = null;

/** Called when a signed-in request comes back 401 (not for sign-in or the session probe). */
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

const SESSION_PROBES = new Set(['/auth/login', '/auth/session']);

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  schema: Parser<T>,
  opts: { query?: Query; body?: unknown; form?: FormData } = {},
): Promise<T> {
  const response = await fetch(`/api/v1${withQuery(path, opts.query)}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    credentials: 'same-origin',
    ...(opts.form
      ? { body: opts.form }
      : opts.body !== undefined
        ? { body: JSON.stringify(opts.body) }
        : {}),
  });
  if (!response.ok) {
    let message = response.statusText || `Request failed (${response.status})`;
    let code: string | null = null;
    let details: Record<string, unknown> = {};
    try {
      const body: unknown = await response.json();
      if (typeof body === 'object' && body !== null) {
        const record = body as Record<string, unknown>;
        if (typeof record.message === 'string') message = record.message;
        if (typeof record.error === 'string') code = record.error;
        details = record;
      }
    } catch {
      // Non-JSON error body: keep the status text.
    }
    if (response.status === 401 && !SESSION_PROBES.has(path.split('?')[0] ?? path)) {
      unauthorizedHandler?.();
    }
    throw new ApiError(response.status, message, code, details);
  }
  const data: unknown = await response.json();
  return schema.parse(data);
}

export function apiGet<T>(path: string, schema: Parser<T>, query?: Query): Promise<T> {
  return request('GET', path, schema, query ? { query } : {});
}

export function apiPost<T>(path: string, schema: Parser<T>, body?: unknown): Promise<T> {
  return request('POST', path, schema, body === undefined ? { body: {} } : { body });
}

export function apiPatch<T>(path: string, schema: Parser<T>, body: unknown): Promise<T> {
  return request('PATCH', path, schema, { body });
}

export function apiPut<T>(path: string, schema: Parser<T>, body: unknown): Promise<T> {
  return request('PUT', path, schema, { body });
}

export function apiDelete<T>(path: string, schema: Parser<T>): Promise<T> {
  return request('DELETE', path, schema);
}

/**
 * Multipart upload (Property: certificates, photos, invoices). Put the fields in `form` before
 * the files: the API checks permissions from the fields before it stores any bytes.
 */
export function apiUpload<T>(path: string, schema: Parser<T>, form: FormData): Promise<T> {
  return request('POST', path, schema, { form });
}

/** Where a stored file is served (inline for PDFs and photos). */
export function fileUrl(id: string, download = false): string {
  return `/api/v1/files/${encodeURIComponent(id)}${download ? '?download=1' : ''}`;
}
